import * as React from 'react'
import { type InsuranceQuote } from '#app/data/mockQuotes'
import { Button } from '../../ui/button'
import { Icon } from '../../ui/icon'
import { InfoTooltip } from '../../ui/info-tooltip'
import { Tag } from '../../ui/tag'

interface ComparisonScreenProps {
	onNext: () => void
	onBack: () => void
	data: Record<string, any>
}

interface InsuranceCompany {
	id: string
	name: string
	logo: string
}

interface ComparisonRow {
	label: string
	values: Array<string | boolean>
}

export function ComparisonScreen({
	onNext,
	onBack,
	data,
}: ComparisonScreenProps) {
	const selectedPlan = data.selectedPlan || 'ceibo'
	const [selectedCompany, setSelectedCompany] = React.useState<string | null>(
		null,
	)

	// Referencias para sincronizar scroll
	const scrollRefs = React.useRef<(HTMLDivElement | null)[]>([])
	const isScrollingRef = React.useRef(false)

	// Función para sincronizar todos los scrolls
	const handleScroll = (index: number) => {
		if (isScrollingRef.current) return

		isScrollingRef.current = true
		const scrollLeft = scrollRefs.current[index]?.scrollLeft || 0

		scrollRefs.current.forEach((ref, i) => {
			if (ref && i !== index) {
				ref.scrollLeft = scrollLeft
			}
		})

		requestAnimationFrame(() => {
			isScrollingRef.current = false
		})
	}

	// Obtener las cotizaciones seleccionadas desde el paso anterior
	const quotesToCompare = (data.quotesToCompare || []) as InsuranceQuote[]

	// Construir la lista de compañías desde las cotizaciones
	const allCompanies: InsuranceCompany[] = quotesToCompare.map((quote) => ({
		id: quote.id,
		name: quote.company,
		logo: quote.companyLogo,
	}))

	// Construir las filas de Soporte (para mobile y desktop)
	const soporteRows: ComparisonRow[] = [
		{
			label: 'Cargo de Instalación',
			values: quotesToCompare.map((q) => q.coverage.cargoInstalacion),
		},
		{
			label: 'Garantía de Satisfacción',
			values: quotesToCompare.map((q) => q.coverage.garantiaSatisfaccion),
		},
		{
			label: 'Soporte al Cliente 24/7',
			values: quotesToCompare.map((q) => q.coverage.soporte24_7),
		},
		{
			label: 'Monitoreo de Energía Inteligente',
			values: quotesToCompare.map((q) => q.coverage.monitoreoEnergia),
		},
	]

	// Construir las filas de Suministro de Energía (para mobile y desktop)
	const suministroRows: ComparisonRow[] = [
		{
			label: 'Fuentes de Energía Renovable',
			values: quotesToCompare.map((q) => q.coverage.fuentesRenovables),
		},
		{
			label: 'Compensación de Carbono',
			values: quotesToCompare.map((q) => q.coverage.compensacionCarbono),
		},
	]

	const getPlanName = (planId: string) => {
		const plans: Record<string, string> = {
			ceibo: 'Plan Ceibo',
			jacaranda: 'Plan Jacarandá',
			roble: 'Plan Roble',
			algarrobo: 'Plan Algarrobo',
		}
		return plans[planId] || 'Plan'
	}

	const getPlanBadge = (planId: string) => {
		const badges: Record<string, string> = {
			ceibo: 'Responsabilidad civil',
			jacaranda: 'Terceros',
			roble: 'Terceros full',
			algarrobo: 'Franquicia',
		}
		return badges[planId] || ''
	}

	const renderCellValue = (value: string | boolean) => {
		if (typeof value === 'boolean') {
			return value ? (
				<div className="flex justify-center">
					<div className="flex size-5 items-center justify-center rounded-full bg-green-500 p-[2px]">
						<Icon name="check" className="size-4 text-white" />
					</div>
				</div>
			) : (
				<div className="flex justify-center">
					<Icon name="cross-1" className="size-4 text-neutral-500" />
				</div>
			)
		}
		return (
			<div className="flex w-full justify-center">
				<span className="text-center text-xs leading-[19px] text-neutral-700 md:text-sm">
					{value}
				</span>
			</div>
		)
	}

	const handleCompanySelect = (companyId: string) => {
		setSelectedCompany(companyId)
	}

	const handleFinalize = () => {
		if (selectedCompany) {
			onNext()
		}
	}

	return (
		<div className="flex w-full flex-col gap-6 pb-20 md:gap-8 md:pb-0">
			{/* Header - Solo visible en desktop */}
			<div className="hidden w-full flex-col items-start justify-between gap-3 md:flex md:flex-row md:items-start md:gap-0">
				<div className="flex flex-col gap-2">
					<h2 className="font-parkinsans text-[20px] leading-7 font-medium text-blue-800 md:text-[24px] md:leading-8">
						Comparar coberturas
					</h2>
					<p className="font-parkinsans text-sm leading-6 font-normal text-neutral-900 md:text-base md:leading-[30px]">
						Descubre la mejor opción de seguro con nuestro comparador y elige un
						plan que se adapte a tus necesidades y presupuesto.
					</p>
				</div>
				<InfoTooltip
					title="¿Qué es el sello verde?"
					content={
						<>
							<p className="mb-1">
								Nuestros planes suman acciones de impacto ambiental:
							</p>
							<ul className="ml-[18px] list-disc space-y-0">
								<li>Compensación de huella de carbono.</li>
								<li>Apoyo a programas de educación ambiental.</li>
								<li>Aporte a energías limpias.</li>
							</ul>
						</>
					}
				>
					<Tag
						variant="secondary"
						appearance="solid"
						color="green"
						size="l"
						corner="rounded"
						option="custom"
						className="h-auto gap-2 rounded-lg px-2 py-2"
					>
						<div className="flex items-center gap-1">
							<Icon
								name="natural-food"
								className="size-[18px] text-green-500"
							/>
							<p className="font-parkinsans text-base leading-5 font-medium text-green-500">
								Sello Verde
							</p>
						</div>
						<Icon
							name="question-mark-circled"
							className="size-4 text-green-600"
						/>
					</Tag>
				</InfoTooltip>
			</div>

			{/* Plan selector - Contenedor gris en mobile */}
			<div className="rounded-xl bg-neutral-100 p-4 md:bg-transparent md:p-0">
				<div className="flex flex-wrap items-center gap-2 md:gap-3">
					<div className="flex items-center gap-2">
						<Icon name="natural-food" className="size-5 text-green-500" />
						<p className="font-parkinsans text-sm leading-[22px] font-bold text-blue-800 md:text-base md:leading-5 md:font-medium">
							{getPlanName(selectedPlan)}
						</p>
						<p className="font-parkinsans text-xs leading-[19px] font-normal text-neutral-700 md:text-sm md:leading-5">
							{getPlanBadge(selectedPlan)}
						</p>
					</div>
					<Button
						variant="primary"
						appearance="stroke"
						size="m"
						className="rounded-full px-2 py-1 text-xs md:rounded-lg md:px-4 md:py-2 md:text-sm"
						onClick={onBack}
					>
						Cambiar plan
					</Button>
				</div>
			</div>

			{/* Mobile: Título + Progress bar visual placeholder */}
			<div className="flex flex-col gap-1 md:hidden">
				<div className="flex w-full justify-center">
					<div className="h-2 w-[300px] rounded-lg bg-neutral-200">
						<div className="h-full w-[68%] rounded-lg bg-gradient-to-r from-blue-500 to-green-500" />
					</div>
				</div>
				<h2 className="font-parkinsans border-b border-neutral-400 pb-4 text-center text-lg leading-[30px] font-medium text-blue-800">
					Comparar coberturas
				</h2>
			</div>

			{/* Desktop: Comparison cards */}
			<div className="hidden w-full md:block">
				<div className="w-full overflow-x-auto md:overflow-x-visible">
					<div
						className="grid min-w-[600px] gap-4 md:w-full md:min-w-0"
						style={{
							gridTemplateColumns: `minmax(150px, 1fr) repeat(${allCompanies.length}, 1fr)`,
						}}
					>
						<div />
						{allCompanies.map((company) => (
							<div
								key={company.id}
								className="flex flex-col gap-4 rounded-xl border border-neutral-300 bg-white p-4"
							>
								<div className="flex h-[80px] items-center justify-center rounded-lg bg-neutral-100">
									<img
										src={company.logo}
										alt={company.name}
										className="max-h-[60px] max-w-[140px] object-contain"
									/>
								</div>
								<Button
									variant="primary"
									appearance="solid"
									size="m"
									className="w-full rounded-lg"
									onClick={() => {
										setSelectedCompany(company.id)
										onNext()
									}}
								>
									Seleccionar
								</Button>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Mobile: Company logos with radio buttons - Scrollable horizontal */}
			<div className="flex w-full gap-2 px-4 md:hidden">
				<div className="w-[111px] shrink-0" />
				<div
					ref={(el) => {
						scrollRefs.current[0] = el
					}}
					onScroll={() => handleScroll(0)}
					className="flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
				>
					<div className="flex min-w-min gap-2">
						{allCompanies.map((company) => (
							<button
								key={company.id}
								type="button"
								onClick={() => handleCompanySelect(company.id)}
								className="flex w-[80px] shrink-0 flex-col items-center gap-2"
							>
								<div
									className={`flex aspect-[138/64] w-full items-center justify-center rounded-lg border-[1.5px] bg-neutral-200 ${
										selectedCompany === company.id
											? 'border-blue-500'
											: 'border-transparent'
									}`}
								>
									<img
										src={company.logo}
										alt={company.name}
										className="max-h-[44px] max-w-[55px] object-contain"
									/>
								</div>
								<div
									className={`flex size-[14px] items-center justify-center rounded-full border ${
										selectedCompany === company.id
											? 'border-blue-500 bg-white'
											: 'border-blue-800 bg-white'
									}`}
								>
									{selectedCompany === company.id && (
										<div className="size-[6px] rounded-full bg-blue-500" />
									)}
								</div>
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Comparison table - Mobile */}
			<div className="flex w-full flex-col gap-1 md:hidden">
				{/* Soporte section - Mobile */}
				<div className="flex flex-col gap-1">
					<h3 className="font-parkinsans px-4 text-sm leading-[22px] font-medium text-neutral-700">
						Soporte
					</h3>
					<div className="flex flex-col">
						{soporteRows.map((row, index) => (
							<div
								key={index}
								className="flex gap-2 border-b border-neutral-400 px-4 py-1"
							>
								<div className="font-parkinsans w-[111px] shrink-0 py-1 text-xs leading-[19px] font-normal text-neutral-700">
									{row.label}
								</div>
								<div
									ref={(el) => {
										scrollRefs.current[index + 1] = el
									}}
									onScroll={() => handleScroll(index + 1)}
									className="flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
								>
									<div className="flex min-w-min gap-2">
										{row.values.map((value, valueIndex) => (
											<div
												key={valueIndex}
												className="flex w-[80px] shrink-0 items-center justify-center py-1"
											>
												{renderCellValue(value)}
											</div>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Suministro de Energía section - Mobile */}
				<div className="flex flex-col gap-1">
					<h3 className="font-parkinsans px-4 text-sm leading-[22px] font-medium text-neutral-700">
						Suministro de Energía
					</h3>
					<div className="flex flex-col">
						{suministroRows.map((row, index) => (
							<div
								key={index}
								className="flex gap-2 border-b border-neutral-400 px-4 py-1"
							>
								<div className="font-parkinsans w-[111px] shrink-0 py-1 text-xs leading-[19px] font-normal text-neutral-700">
									{row.label}
								</div>
								<div
									ref={(el) => {
										scrollRefs.current[soporteRows.length + 1 + index] = el
									}}
									onScroll={() => handleScroll(soporteRows.length + 1 + index)}
									className="flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
								>
									<div className="flex min-w-min gap-2">
										{row.values.map((value, valueIndex) => (
											<div
												key={valueIndex}
												className="flex w-[80px] shrink-0 items-center justify-center py-1"
											>
												{renderCellValue(value)}
											</div>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Comparison table - Desktop */}
			<div className="hidden w-full flex-col gap-6 md:flex">
				{/* Soporte section - Desktop */}
				<div className="flex flex-col gap-3">
					<h3 className="font-parkinsans text-base leading-6 font-medium text-blue-800">
						Soporte
					</h3>
					<div className="flex flex-col gap-2">
						{soporteRows.map((row, index) => (
							<div
								key={index}
								className="grid gap-4 rounded-lg border border-neutral-200 bg-white p-4"
								style={{
									gridTemplateColumns: `minmax(150px, 1fr) repeat(${allCompanies.length}, 1fr)`,
								}}
							>
								<div className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
									{row.label}
								</div>
								{row.values.map((value, valueIndex) => (
									<div
										key={valueIndex}
										className="flex items-center justify-center"
									>
										{renderCellValue(value)}
									</div>
								))}
							</div>
						))}
					</div>
				</div>

				{/* Suministro de Energía section - Desktop */}
				<div className="flex flex-col gap-3">
					<h3 className="font-parkinsans text-base leading-6 font-medium text-blue-800">
						Suministro de Energía
					</h3>
					<div className="flex flex-col gap-2">
						{suministroRows.map((row, index) => (
							<div
								key={index}
								className="grid gap-4 rounded-lg border border-neutral-200 bg-white p-4"
								style={{
									gridTemplateColumns: `minmax(150px, 1fr) repeat(${allCompanies.length}, 1fr)`,
								}}
							>
								<div className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
									{row.label}
								</div>
								{row.values.map((value, valueIndex) => (
									<div
										key={valueIndex}
										className="flex items-center justify-center"
									>
										{renderCellValue(value)}
									</div>
								))}
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Footer buttons - Desktop only */}
			<div className="hidden w-full flex-col-reverse items-center justify-between gap-3 md:flex md:flex-row md:gap-0">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="primary"
					appearance="solid"
					size="l"
					onClick={handleFinalize}
					disabled={!selectedCompany}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					Finalizar
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>

			{/* Mobile footer - Fixed at bottom */}
			<div className="fixed right-0 bottom-0 left-0 z-10 border-t border-neutral-400 bg-white px-6 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<Button
						variant="primary"
						appearance="ghost"
						size="m"
						onClick={onBack}
						className="gap-2 rounded-xl px-4 py-3"
					>
						<Icon name="arrow-left" className="size-4" />
						Atrás
					</Button>
					<Button
						variant="primary"
						appearance="solid"
						size="m"
						onClick={handleFinalize}
						disabled={!selectedCompany}
						className="gap-2 rounded-xl px-4 py-3"
					>
						Finalizar
						<Icon name="arrow-right" className="size-3.5" />
					</Button>
				</div>
			</div>
		</div>
	)
}
